import React, { useMemo, useState } from "react";
import moment from "moment";

import {
  DSModal,
  DSTabs,
  DSFieldLabel,
  DSDateInput,
  DSQuickDateGroup,
  DSInfoBanner,
  DSButton,
} from "./ds";
import BranchSelectModal from "./BranchSelectModal";
import { PeriodOptions } from "./Elements";
import { getDateShortRange } from "../helpers/Utils";
import Constants from "../helpers/Constants";

const TAB_QUERIED = "queried";
const TAB_LOGISTICS = "logistics";
const TAB_LOSS_DAMAGE = "lossDamage";

// 서버 리포트 item 이름 (sales-server LS_sendReport 계약)
const REPORT_ITEMS = {
  [TAB_LOGISTICS]: "logisticsHistories",
  [TAB_LOSS_DAMAGE]: "lossDamageHistories",
};

const REPORT_NAMES = {
  [TAB_LOGISTICS]: "물류이동 리포트",
  [TAB_LOSS_DAMAGE]: "손실/불량 리포트",
};

// 히스토리 필터와 동일한 기간 퀵버튼 (우진 지시 2026-07-15: 1주일/1개월/3개월 → 표준 PeriodOptions)
const QUICK_DATE_OPTIONS = PeriodOptions.map((option) => ({
  value: option.key,
  label: option.text,
}));

const HELPER_TEXT_STYLE = {
  fontFamily: "Pretendard",
  fontSize: 12,
  lineHeight: "18px",
  color: "var(--ds-text-tertiary)",
};

const SUMMARY_LABEL_STYLE = { width: 96, flexShrink: 0 };

// DSDateInput은 UTC 자정 Date를 넘기므로 로컬 자정으로 정규화한다 (서쪽 타임존에서 하루 밀림 방지)
const normalizePickedDate = (date) =>
  date
    ? moment(moment.utc(date).format("YYYY-MM-DD"), "YYYY-MM-DD").toDate()
    : null;

const SummaryRow = ({ label, value }) => (
  <div style={{ display: "flex", gap: 8 }}>
    <span className="ds-t-body-s" style={SUMMARY_LABEL_STYLE}>
      {label}
    </span>
    <span className="ds-t-body-s-sub ds-fc-secondary">{value}</span>
  </div>
);

/**
 * 재고 히스토리 다운로드 리포트 선택 모달.
 *
 * - 탭1 "조회한 히스토리": 리스트의 현재 조회 조건 요약만 보여주고,
 *   다운로드는 기존 로직(onDownloadQueried → exportCsv, legacy 분기 포함)에 그대로 위임한다.
 * - 탭2 "물류이동 리포트" / 탭3 "손실/불량 리포트": 지점(멀티/전체) + 기간(최대 3개월)을
 *   입력받아 onRequestReport(item, filters, reportName)로 넘긴다.
 *   이메일 확인/실제 호출은 부모(기존 sendMailModal 플로우 + requestReportDownload)가 담당하므로
 *   이 모달은 호출·수신 방식을 모른다.
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {Function} onDownloadQueried - 탭1 다운로드 (기존 플로우 위임)
 * @param {Function} onRequestReport - (item, filters, reportName) 탭2·3 요청 확정
 * @param {boolean} showReportTabs - 탭2·3 노출 여부 (히스토리 탭과 동일한 isRetail 게이팅)
 * @param {Array<{value: string, text: string}>} locations - 선택 가능한 지점 옵션
 * @param {string|null} lockedLocationId - 지점 선택 권한이 없을 때 고정되는 지점 id
 * @param {{period: string, location: string, type: string, search: string|null, view: string}} queriedSummary
 */
const StockReportDownloadModal = ({
  open,
  onClose,
  onDownloadQueried,
  onRequestReport,
  showReportTabs,
  locations,
  lockedLocationId,
  queriedSummary,
}) => {
  const [activeTab, setActiveTab] = useState(TAB_QUERIED);
  // null = 아직 손대지 않음 → 전체 지점 선택 상태로 취급
  const [selectedLocationIds, setSelectedLocationIds] = useState(null);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(() =>
    moment().subtract(1, "months").toDate(),
  );
  const [endDate, setEndDate] = useState(() => new Date());
  const [quickDateValue, setQuickDateValue] = useState(null);

  const tabItems = useMemo(() => {
    const items = [{ value: TAB_QUERIED, label: "조회한 히스토리" }];
    if (showReportTabs) {
      items.push(
        { value: TAB_LOGISTICS, label: REPORT_NAMES[TAB_LOGISTICS] },
        { value: TAB_LOSS_DAMAGE, label: REPORT_NAMES[TAB_LOSS_DAMAGE] },
      );
    }
    return items;
  }, [showReportTabs]);

  const allLocationIds = useMemo(
    () => (locations || []).map((loc) => loc.value),
    [locations],
  );
  // 선택값을 현재 옵션과 교집합해 stale id를 걸러낸다 (전체 선택 판정의 전제)
  const effectiveLocationIds = useMemo(() => {
    if (lockedLocationId) {
      return [lockedLocationId];
    }
    if (selectedLocationIds == null) {
      return allLocationIds;
    }
    const available = new Set(allLocationIds);
    return selectedLocationIds.filter((id) => available.has(id));
  }, [lockedLocationId, selectedLocationIds, allLocationIds]);

  const isQueriedTab = activeTab === TAB_QUERIED;
  const isValidRange =
    startDate && endDate && moment(startDate).isSameOrBefore(endDate, "day");
  const canDownload =
    isQueriedTab || (effectiveLocationIds.length > 0 && isValidRange);

  const isAllSelected =
    !lockedLocationId &&
    allLocationIds.length > 0 &&
    effectiveLocationIds.length === allLocationIds.length;
  const lockedLocation = lockedLocationId
    ? (locations || []).find((loc) => loc.value === lockedLocationId)
    : null;
  const locationSummary = lockedLocationId
    ? lockedLocation?.text || lockedLocation?.name || "현재 지점"
    : isAllSelected
      ? `전체 지점 (${allLocationIds.length}개)`
      : effectiveLocationIds.length > 0
        ? `${effectiveLocationIds.length}개 지점 선택`
        : "지점을 선택해 주세요";

  // 최대 3개월 초과 입력 시 반대편 날짜를 보정한다.
  const handleStartDateChange = (picked) => {
    const date = normalizePickedDate(picked);
    setQuickDateValue(null);
    setStartDate(date);
    if (
      date &&
      endDate &&
      moment(endDate).diff(moment(date), "months", true) >
        Constants.REPORT_RESTRICTION_MONTH
    ) {
      setEndDate(
        moment(date).add(Constants.REPORT_RESTRICTION_MONTH, "months").toDate(),
      );
    }
  };

  const handleEndDateChange = (picked) => {
    const date = normalizePickedDate(picked);
    setQuickDateValue(null);
    setEndDate(date);
    if (
      date &&
      startDate &&
      moment(date).diff(moment(startDate), "months", true) >
        Constants.REPORT_RESTRICTION_MONTH
    ) {
      setStartDate(
        moment(date)
          .subtract(Constants.REPORT_RESTRICTION_MONTH, "months")
          .toDate(),
      );
    }
  };

  const handleQuickDateChange = (value) => {
    setQuickDateValue(value);
    const { startDate: rangeStart, endDate: rangeEnd } =
      getDateShortRange(value);
    setStartDate(rangeStart);
    setEndDate(rangeEnd);
  };

  const handleDownload = () => {
    if (isQueriedTab) {
      onDownloadQueried();
      return;
    }
    const filters = [];
    // 전체 지점 선택 시에는 locationIds 필터 자체를 넣지 않는다 (서버 계약)
    if (!isAllSelected) {
      filters.push({
        key: "locationIds",
        operation: "in",
        value: effectiveLocationIds,
      });
    }
    const s = new Date(startDate);
    const e = new Date(endDate);
    s.setHours(0, 0, 0, 0);
    e.setHours(23, 59, 59, 999);
    filters.push({
      key: "createdAt",
      operation: "between",
      value: [s.toISOString(), e.toISOString()],
    });
    onRequestReport(REPORT_ITEMS[activeTab], filters, REPORT_NAMES[activeTab]);
  };

  return (
    <>
      <DSModal
        open={open}
        onClose={onClose}
        title="리포트 다운로드"
        width={560}
        height="auto"
        hideTitleDivider
      >
        <div
          style={{
            // figma 1100:23505: 헤더(구분선 없음) 아래 탭까지 12px 간격
            padding: "12px 24px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
        <DSTabs
          className="ds-tabs--pill"
          items={tabItems}
          activeValue={activeTab}
          onChange={setActiveTab}
        />
        {/* 탭 전환 시 모달 높이가 출렁이지 않도록 콘텐츠 영역 최소높이 고정.
            가장 큰 탭(리포트: 지점+기간 = 실측 282px) 기준. 더 길어지면 자연 확장 (우진 지시: minHeight). */}
        <div style={{ minHeight: 290 }}>
          {isQueriedTab ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <SummaryRow label="조회 기간" value={queriedSummary?.period || "-"} />
            <SummaryRow label="조회 지점" value={queriedSummary?.location || "-"} />
            <SummaryRow label="조정 타입" value={queriedSummary?.type || "-"} />
            {queriedSummary?.search ? (
              <SummaryRow label="검색어" value={queriedSummary.search} />
            ) : null}
            <SummaryRow label="보기 방식" value={queriedSummary?.view || "-"} />
            <div style={HELPER_TEXT_STYLE}>
              {"현재 조회한 조건 그대로 다운로드됩니다."}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <DSFieldLabel required width="auto">
                조회 지점
              </DSFieldLabel>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className="ds-t-body-s-sub">{locationSummary}</span>
                {!lockedLocationId ? (
                  <DSButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setBranchModalOpen(true)}
                  >
                    지점 선택
                  </DSButton>
                ) : null}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <DSFieldLabel required width="auto">
                조회 기간
              </DSFieldLabel>
              <div className="ds-date-range">
                <DSDateInput
                  label="시작일"
                  value={startDate}
                  onChange={handleStartDateChange}
                />
                <span className="ds-date-range__sep">~</span>
                <DSDateInput
                  label="종료일"
                  value={endDate}
                  onChange={handleEndDateChange}
                />
              </div>
              <DSQuickDateGroup
                className="ds-quick-date--fill"
                options={QUICK_DATE_OPTIONS}
                activeValue={quickDateValue}
                onChange={handleQuickDateChange}
              />
              <div style={HELPER_TEXT_STYLE}>
                {"최대 3개월까지 조회할 수 있습니다."}
              </div>
            </div>
          </div>
          )}
        </div>
        <DSInfoBanner>
            데이터가 많은 경우 파일이 준비되면 이메일로 발송됩니다.
          </DSInfoBanner>
        </div>
        <div
          style={{
            padding: 16,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            borderTop: "1px solid var(--ds-border-default)",
            flexShrink: 0,
          }}
        >
          <DSButton variant="secondary" size="lg" onClick={onClose}>
            취소
          </DSButton>
          <DSButton
            variant="primary"
            size="lg"
            onClick={handleDownload}
            disabled={!canDownload}
          >
            다운로드
          </DSButton>
        </div>
      </DSModal>
      {branchModalOpen ? (
        <BranchSelectModal
          open={branchModalOpen}
          onClose={() => setBranchModalOpen(false)}
          locations={locations || []}
          selectedIds={effectiveLocationIds}
          onApply={(ids) => {
            setSelectedLocationIds(ids);
            setBranchModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
};

export default StockReportDownloadModal;
